import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, BookOpen, CheckCircle, Clock } from 'lucide-react';

const API = '/api';

export const EducationPage = () => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await axios.get(`${API}/education/courses`);
        setCourses(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchCourses();
  }, []);

  const openCourse = async (courseId) => {
    try {
      const res = await axios.get(`${API}/education/courses/${courseId}`);
      setSelectedCourse(res.data);
    } catch (e) { console.error(e); }
  };

  const markComplete = async (lessonId) => {
    try {
      await axios.post(`${API}/education/progress/${lessonId}/complete`);
      // Refresh course
      if (selectedCourse) openCourse(selectedCourse.course_id);
    } catch (e) { console.error(e); }
  };

  const getYouTubeId = (url) => {
    if (!url) return '';
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
    return match ? match[1] : '';
  };

  if (selectedCourse) {
    const completedLessons = selectedCourse.progress?.completed_lessons || [];
    return (
      <div className="min-h-screen bg-[#0a0a0f] p-4 md:p-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => setSelectedCourse(null)} className="text-gray-400 hover:text-white mb-6 flex items-center gap-2">
            ← Retounen nan kou yo
          </button>
          
          <h1 className="text-3xl font-bold text-white mb-2">{selectedCourse.title}</h1>
          <p className="text-gray-400 mb-8">{selectedCourse.description}</p>

          <div className="space-y-4">
            {selectedCourse.lessons?.map((lesson, i) => {
              const isCompleted = completedLessons.includes(lesson.lesson_id);
              const videoId = getYouTubeId(lesson.youtube_url);
              return (
                <div key={lesson.lesson_id} className="bg-[#12121a] rounded-xl border border-[#2d2d3a] overflow-hidden">
                  <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => document.getElementById(`video-${lesson.lesson_id}`)?.scrollIntoView({ behavior: 'smooth' })}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-green-500/20' : 'bg-yellow-400/20'}`}>
                      {isCompleted ? <CheckCircle className="w-5 h-5 text-green-400" /> : <span className="text-yellow-400 font-bold">{i + 1}</span>}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-medium">{lesson.title}</h3>
                      <p className="text-gray-500 text-sm">{lesson.description}</p>
                    </div>
                    {lesson.duration_minutes > 0 && (
                      <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />{lesson.duration_minutes} min</span>
                    )}
                  </div>
                  
                  {videoId && (
                    <div id={`video-${lesson.lesson_id}`} className="px-4 pb-4">
                      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                        <iframe
                          className="absolute top-0 left-0 w-full h-full rounded-lg"
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title={lesson.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                      {!isCompleted && (
                        <button
                          onClick={() => markComplete(lesson.lesson_id)}
                          className="mt-3 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 text-sm font-medium border border-green-500/30"
                        >
                          <CheckCircle className="w-4 h-4 inline mr-2" />Make Fini
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <div className="bg-gradient-to-b from-[#1a1a2e] to-[#0a0a0f] py-16 px-4 text-center">
        <h1 data-testid="education-title" className="text-4xl font-extrabold text-white mb-3">
          <BookOpen className="inline w-10 h-10 text-yellow-400 mr-3" />
          Edikasyon
        </h1>
        <p className="text-gray-400 max-w-xl mx-auto">Aprann ak videyo pou devlope biznis ou</p>
      </div>

      <div className="max-w-5xl mx-auto px-4 pb-20">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-20 h-20 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Kou yo ap disponib byento!</p>
            <p className="text-gray-600 text-sm mt-2">Ekip nou ap prepare kontni edikasyon pou ou.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map(course => (
              <div
                key={course.course_id}
                data-testid={`course-${course.course_id}`}
                onClick={() => openCourse(course.course_id)}
                className="bg-[#12121a] rounded-xl border border-[#2d2d3a] overflow-hidden hover:border-yellow-400/30 transition-all cursor-pointer group"
              >
                {course.thumbnail_url ? (
                  <img src={course.thumbnail_url} alt={course.title} className="w-full h-40 object-cover" />
                ) : (
                  <div className="w-full h-40 bg-[#1a1a2e] flex items-center justify-center">
                    <Play className="w-12 h-12 text-gray-700 group-hover:text-yellow-400 transition-colors" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="text-white font-semibold mb-2 group-hover:text-yellow-400 transition-colors">{course.title}</h3>
                  <p className="text-gray-500 text-sm line-clamp-2">{course.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
